<script setup>
import {onMounted, ref} from 'vue';
import {getPublicFundraiser} from "@/services/api_services";
import { formatDate} from "@/utils/formatters";
import {useRoute, useRouter} from "vue-router";

const fundraiser = ref(null);
const loading = ref(true);
const error = ref(null);
const router = useRouter();
const route = useRoute();
const fundraiserId = route.params.id;

const expandedSections = ref({
  progress: false,
  dates: false
});

function goToContributePage(id) {
  router.push({ name: 'ContributePage', params: { id } });
}

function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section];
}

onMounted(async () => {
  try {
    fundraiser.value = await getPublicFundraiser(fundraiserId);
  } catch (e) {
    console.error(e);
    error.value = "Failed to load fundraiser details. Please try again later.";
  } finally {
    loading.value = false;
  }
});
</script>




<template>

  <div class="fundraiser-container">
    <!-- Loading state -->
    <a href="https://givva.world" target="_blank" rel="noopener noreferrer" class="givva-banner">
      <img src="@/assets/logo.png" alt="Givva" class="givva-logo" />
      <span class="givva-text">Powered by <span class="givva-link">Givva Wealthtech Limited</span></span>
    </a>
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading fundraiser details...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">!</div>
      <p>{{ error }}</p>
      <button @click="window.location.reload()" class="retry-button">Retry</button>
    </div>

    <div v-else-if="fundraiser" class="fundraiser-details">
<!--      &lt;!&ndash; Header section (unchanged) &ndash;&gt;-->
<!--      <header class="fundraiser-header">-->
<!--        <h1 class="fundraiser-title">{{ fundraiser.name }}</h1>-->
<!--        <p class="fundraiser-identifier">#{{ fundraiser.identifier }}</p>-->
<!--        <p class="fundraiser-community">By {{ fundraiser.communityName }}</p>-->
<!--      </header>-->

      <!-- Main content -->
      <div class="fundraiser-content">
        <!-- Image section (unchanged) -->
        <div class="image-section">
          <img
              :src="fundraiser.images"
              :alt="`Image for ${fundraiser.name}`"
              class="fundraiser-image"
              @error="fundraiser.images = '/placeholder-image.jpg'"
          />
        </div>

        <!-- Description section (unchanged) -->
        <div class="description-section">
          <h2 class="section-title">{{fundraiser.name}} Event</h2>
          <p class="short-description">{{ fundraiser.shortDescription }}</p>
                  <p class="fundraiser-community">By {{ fundraiser.communityName }}</p>
        </div>

        <!-- Show ONLY if allowsDirectWithdrawals is missing OR true -->
        <div
            v-if="fundraiser.allowsDirectWithdrawals === undefined
        || fundraiser.allowsDirectWithdrawals === null
        || fundraiser.allowsDirectWithdrawals === true"
        >
          <!-- Expansion Tile for Progress -->
          <div class="expansion-tile" :class="{ 'is-expanded': expandedSections.progress }">
            <div class="expansion-tile-header" @click="toggleSection('progress')">
              <h2 class="section-title">Contribution Progress</h2>
              <svg class="expansion-icon" viewBox="0 0 24 24" :class="{ 'expanded': expandedSections.progress }">
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
              </svg>
            </div>
            <transition name="expand">
              <div class="expansion-tile-content" v-show="expandedSections.progress">
                <div class="progress-bar-container">
                  <div
                      class="progress-bar"
                      :style="{ width: `${(fundraiser.totalContributions / fundraiser.targetAmount) * 100}%` }"
                  ></div>
                </div>
                <div class="progress-stats">
                  <div class="stat">
                    <span class="stat-value">{{ Math.round((fundraiser.totalContributions / fundraiser.targetAmount) * 100) }}%</span>
                    <span class="stat-label">Funded</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Expansion Tile for Dates -->
          <div class="expansion-tile" :class="{ 'is-expanded': expandedSections.dates }">
            <div class="expansion-tile-header" @click="toggleSection('dates')">
              <h2 class="section-title">Event Dates</h2>
              <svg class="expansion-icon" viewBox="0 0 24 24" :class="{ 'expanded': expandedSections.dates }">
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
              </svg>
            </div>
            <transition name="expand">
              <div class="expansion-tile-content" v-show="expandedSections.dates">
                <div class="detail-item">
                  <span class="detail-label">Start Date:</span>
                  <span class="detail-value">{{ formatDate(fundraiser.startDate) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Deadline:</span>
                  <span class="detail-value">{{ formatDate(fundraiser.deadline) }}</span>
                </div>
              </div>
            </transition>
          </div>
        </div>


        <!-- Action buttons (unchanged) -->
        <div class="action-buttons">
          <button @click="goToContributePage(fundraiserId)" class="donate-button">Contribute / Pledge</button>
        </div>

        <!-- Footer -->
        <div class="givva-footer">
          <a
              href="https://play.google.com/store/apps/details?id=com.GIVVA.givva&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              class="givva-footer-link"
          >
            © 2026 Givva Wealthtech Limited
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fundraiser-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #1f2937;
  min-height: 100vh;
  background: linear-gradient(
      135deg,
      rgba(203, 217, 196, 1) 0%,
      rgba(249, 247, 194, 1) 50%,
      rgba(249, 213, 206, 1) 100%
  );
}

.expansion-tile {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.expansion-tile.is-expanded {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.givva-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  text-decoration: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.givva-logo {
  height: 38px;
  width: auto;
  object-fit: contain;
}
.givva-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding: 1rem 0;
}

.givva-footer-link {
  font-size: 13px;
  color: black;
  text-decoration: underline;
  transition: color 0.3s ease;
}

.givva-footer-link:hover {
  color: #14b8a6;
}

.givva-text {
  font-size: 13px;
  font-weight: 600;
  color: #000000;
  letter-spacing: 0.3px;
}

.givva-link {
  color: #2563eb;
}

.expansion-tile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.expansion-tile-header:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.expansion-icon {
  width: 24px;
  height: 24px;
  fill: currentColor;
  transition: transform 0.3s ease;
}

.expansion-icon.expanded {
  transform: rotate(180deg);
}

.expansion-tile-content {
  padding: 0 1.75rem 1.75rem;
}


.detail-item {
  flex: 1;
  min-width: 200px;
}
.detail-label {
  font-weight: 500;
  margin-right: 8px;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  border-radius: 12px;
  padding: 2rem;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #14b8a6;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  background-color: #ef4444;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 1rem;
  font-weight: bold;
}

.retry-button {
  background-color: #14b8a6;
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.retry-button:hover {
  background-color: #14b8a6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.fundraiser-details {
  border-radius: 16px;
  overflow: hidden;
  padding: 2.5rem;
  margin-top: 1rem;
}

.fundraiser-header {
  text-align: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.fundraiser-title {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #000000;
  line-height: 1.2;
}

.fundraiser-identifier {
  font-size: 0.95rem;
  color: #6b7280;
  letter-spacing: 0.5px;
}

.fundraiser-community {
  font-size:12px;
  color: #14b8a6;
  font-weight: 500;
  margin-top: 0.5rem;
}

.fundraiser-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.image-section {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.image-section:hover {
  transform: translateY(-2px);
}

.fundraiser-image {
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  display: block;
  border-radius: 12px;
}

.section-title {
  font-size: 15px;
  margin-bottom: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  position: relative;
  padding-bottom: 0.75rem;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #000000, #000000);
  border-radius: 3px;
}

.short-description {
  font-size: 15px;
  line-height: 1.75;
  color: #374151;
}

.progress-section, .details-section {
  padding: 1.75rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.2);
}

.progress-bar-container {
  height: 10px;
  background-color: rgba(243, 244, 246, 0.5);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(to right, #14b8a6, #14b8a6);
  transition: width 0.5s ease;
  border-radius: 10px;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.stat {
  text-align: center;
  flex: 1 1 120px;
  padding: 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.3);
}

.stat:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.4);
}

.stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #000000;
  display: block;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.detail-item {
  font-size: 1rem;
  padding: 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.3);
}

.detail-item:hover {
  transform: translateX(3px);
  background-color: rgba(255, 255, 255, 0.4);
}

.detail-label {
  font-weight: 600;
  color: #4b5563;
  margin-right: 0.5rem;
}

.detail-value {
  color: #1f2937;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.donate-button, .share-button {
  padding: 0.85rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 200px;
  text-align: center;
  letter-spacing: 0.5px;
}

.donate-button {
  background: linear-gradient(to right, #14b8a6, #14b8a6);
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.25);
}

.donate-button:hover {
  background: linear-gradient(to right, #14b8a6, #14b8a6);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.3);
}

.share-button {
  background-color: rgba(255, 255, 255, 0.8);
  color: #14b8a6;
  border: 2px solid #14b8a6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.share-button:hover {
  background-color: rgba(240, 244, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive styles for smaller devices */
@media (max-width: 768px) {
  .fundraiser-container {
    padding: 1rem;
  }

  .fundraiser-details {
    padding: 1.5rem;
  }

  .fundraiser-title {
    font-size: 2rem;
  }

  .fundraiser-image {
    max-height: 300px; /* Reduced from 500px */
  }

  .progress-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .stat {
    flex: 1 1 auto;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .donate-button, .share-button {
    width: 100%;
  }
}

/* Extra small devices (phones) */
@media (max-width: 480px) {
  .fundraiser-image {
    max-height: 250px; /* Even smaller for very small devices */
    max-width: 300px;
  }

  .fundraiser-title {
    font-size: 1.75rem;
  }

  .section-title {
    font-size: 15px;
  }

  .short-description {
    font-size: 15px;
  }
}
</style>